import moment from 'moment'
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react'
import LoadingSpinner from '../../../../../../../baseComponents/LoaderDecider/LoadingSpinner'
import TableWithoutVirtualListing from '../../../../../../../baseComponents/TwinTable/TableWithoutVirtualListing'
import { CustomRenderString } from '../../../../../../../baseComponents/TwinTable/VirtualTableListing/CustomRenders'
import CustomSelect, { CustomSelectWithSearchBar } from '../../../../../../../forms/CustomSelect'
import { InputCalendarStateFull } from '../../../../../../../forms/Input/InputCalendar'
import SearchBarProduct from '../../../../../../../specificComponents/Product/SearchBarProduct'
import { twinFetchPostJSON } from '../../../../../../../utils/globals/data'
import { addDictionaryExtraLine, TwinDictionary } from '../../../../../../../utils/globals/dictionary'
import useTwinTranslation from '../../../../../../../utils/hooks/useTwinTranslation'
import { parseSellStatsByProduct, parseSellStatsByEmployee, parseSellStatsByCategory } from './functions'
import { SellStatsEmployeeData, SellStatsProductData, TypeStatsTy, TypeSellTy, TypeSellKeyTy, TypeSellTableNameTy } from './types'
import useConfigColumns, { SelectedColumnsAndFilters } from '../../../../../../../utils/hooks/useConfigColumns'
import { ProductExtendedTyShort } from '../../../../../../../specificComponents/Product/types'
import useSellStatsColumns, { useSellStatsColumnsShort } from '../../../../../../../utils/hooks/sells/orders/useSellStatsColumns'
import { flushSync } from 'react-dom'
import { EmployeesPayload } from '../../../../../../../utils/reducers/company/employees'
import { connect, ConnectedProps } from 'react-redux'
import { AllReduxPayloads } from '../../../../../../../utils/reducers'
import withLoading from '../../../../../../../utils/hoc/withLoading'
import { ButtonModalConfigStatsEmployee, ButtonModalConfigStatsProduct } from './ButtonModalConfigStats'
import useSellStatsUrls from '../../../../../../../utils/hooks/sells/orders/useSellStatsUrls'
import './sellstats.sass'
import { CustomerProjectTypePayload } from '../../../../../../../utils/reducers/customers/customerProjectTypes'
import { getAllCustomerProjectTypes } from '../../../../../../../utils/reducers/getters'
import { emptyExtraLineFirstSortFunction } from '../../../../../../../forms/CustomSelect/functions'
import { DisplayPriceGettingParams } from '../../../../../../../baseComponents/Displays/DisplayPrice'

interface SellStatsBodyProps {
    extraTableProps?: TwinDictionary
}

const SellStatsBody: React.FC<SellStatsBodyProps> = ({ extraTableProps }) => {
    const { typeStats, changeTypeStats } = useSellStatsBody()
    const componentsDict: Record<TypeStatsTy, React.FC<SellStatsBodyBaseComponentsProps>> = {
        EmployeeId: SellsStatsBodyEmployees,
        ProductId: SellsStatsBodyProduct,
        CategoryId: SellsStatsBodyCategory,
    }
    const typeSellKey = extraTableProps?.typeSell as TypeSellKeyTy || 0 
    const sellStatsUrls = useSellStatsUrls()
    const dictTypeSells: Record<TypeSellKeyTy, { typeSell: TypeSellTy, typeTableName: TypeSellTableNameTy }> = {
        0: { typeSell: 'QuoteOrder', typeTableName: 'quote'},
        1: { typeSell: 'SellOrder', typeTableName: 'sell_order'},
        2: { typeSell: 'SellDeliveryNote', typeTableName: 'sell_delivery_note'},
        3: { typeSell: 'SellInvoice', typeTableName: 'sell_invoice'},
        4: { typeSell: 'SellTicket', typeTableName: 'sell_ticket'},
    }
    const typeTableName = dictTypeSells?.[typeSellKey]?.typeTableName
    const typeSell = dictTypeSells?.[typeSellKey]?.typeSell
    const Component = componentsDict[typeStats]
    return <div className='sell_stats flex flex-auto flex-col'>
        <Component urlGetData={sellStatsUrls?.[typeSell]?.[typeStats] || ''} {...{ typeStats, changeTypeStats, typeSell, typeTableName }} />
    </div>
}


const useSellStatsBody = () => {
    const [typeStats, setTypeStats] = useState<TypeStatsTy>('EmployeeId')

    const changeTypeStats = useCallback((value: TypeStatsTy) => {
        if (value !== typeStats) {
            setTypeStats(value)
        }
    }, [setTypeStats, typeStats])
    return { typeStats, changeTypeStats }
}

interface SellStatsBodyBaseComponentsProps  {
    typeTableName: TypeSellTableNameTy
    typeSell: TypeSellTy
    typeStats: TypeStatsTy
    changeTypeStats: (value: TypeStatsTy) => void
    urlGetData: string
}

interface SellsStatsBodyEmployeesProps extends SellStatsBodyBaseComponentsProps { }

const SellsStatsBodyEmployees: React.FC<SellsStatsBodyEmployeesProps> = ({ typeStats, changeTypeStats, typeTableName, ...logic }) => {
    const translations = useSellStatsColumns()
    const { selectedColumnsAndFilters, setSelectedColumnsAndFilters } = useConfigColumns({ name: 'employee_' + typeTableName + '_config_stats', columns: [{ id: 'row', dataKey: 'row', label: '' }] })
    const { data, getData } = useSellsStatsBodyEmployeesLogic({ selectedColumnsAndFilters, ...logic })
    const myData = useMemo(() => parseSellStatsByEmployee(data?.selectedEmployees || [], selectedColumnsAndFilters, typeTableName), [data?.selectedEmployees, selectedColumnsAndFilters, typeTableName ])
    myData.columns[0].customRender = (parameterValue) => <CustomRenderString value={translations[parameterValue]} />
    let renderThis: JSX.Element = <LoadingSpinner className='h-full' />
    if (data?.selectedEmployees) {
        renderThis = <TableWithoutVirtualListing data={myData.data} columns={myData.columns} />
    }

    return (
        <Fragment>
            <div className='px-20 pt-10 flex-auto h-1 flex-col flex'>
                <SellStatsHeader {...{ getData, selectedColumnsAndFilters, setSelectedColumnsAndFilters, typeStats, changeTypeStats, typeTableName }} children={
                    <ButtonModalConfigStatsEmployee {...{ setSelectedColumnsAndFilters, selectedColumnsAndFilters, typeStats, typeTableName }} />
                } />
                <div className='flex-auto mt-20 '>
                    {renderThis}
                </div>
            </div>
            <SellStatsBodyTotals myTotalAmountDict={myData.myTotalAmountDict} myData={myData.data} CurrencyId={myData.CurrencyId} />
        </Fragment>
    )
}

interface SellsStatsBodyEmployeesLogicProps {
    typeSell: TypeSellTy
    urlGetData: string
    selectedColumnsAndFilters: SelectedColumnsAndFilters
}

const useSellsStatsBodyEmployeesLogic = ({ selectedColumnsAndFilters, urlGetData, typeSell }: SellsStatsBodyEmployeesLogicProps) => {
    const [data, setData] = useState<SellStatsEmployeeData | null>(null)

    const getData = useCallback(async () => {
        const haveFiltersValue = selectedColumnsAndFilters.filters?.EmployeeId?.value.length > 0
        const myIds = haveFiltersValue ? selectedColumnsAndFilters.filters?.EmployeeId?.value : []
        let parseWhere: TwinDictionary = {
            'is_last': true
        }
        if (myIds?.length) {
            parseWhere.RepresentativeEmployeeId = {
                '$in': myIds,
            }
        }

        const startDate = selectedColumnsAndFilters.filters?.startDate?.value
        const endDate = selectedColumnsAndFilters.filters?.endDate?.value
        if (startDate && endDate) {
            parseWhere[typeSell+'.createdAt'] = { '$betweenN': [new Date(startDate), new Date(endDate)] }
        } else if (startDate) {
            parseWhere[typeSell+'.createdAt'] = { '$gteN': new Date(startDate) }
        } else if (endDate) {
            parseWhere[typeSell+'.createdAt'] = { '$lteN': new Date(endDate) }
        }
        const projectType = selectedColumnsAndFilters.filters?.projectType?.value
        if (projectType) {
            parseWhere['CustomerProject.CustomerProjectTypeId'] = projectType
        }

        let result = await twinFetchPostJSON(urlGetData, {
            where: parseWhere,
            order: [['createdAt', 'ASC']]
        })
        if (result) {
            setData((old) => {
                const copy = JSON.parse(JSON.stringify(old))
                return { ...copy, selectedEmployees: result }
            })
        }
    }, [selectedColumnsAndFilters, typeSell, urlGetData])

    useEffect(() => {
        getData()
    }, [getData])

    return { data, getData, setData }
}

interface SellsStatsBodyProductProps extends SellStatsBodyBaseComponentsProps { }

const SellsStatsBodyProduct: React.FC<SellsStatsBodyProductProps> = ({ typeStats, changeTypeStats, typeTableName, ...logic }) => {
    const { t } = useTwinTranslation()
    const translations = useSellStatsColumns()
    const { selectedColumnsAndFilters, setSelectedColumnsAndFilters } = useConfigColumns({ name: typeTableName +'_product_config_stats', columns: [{ id: 'row', dataKey: 'row', label: '' }] })
    const { data, getData, productsSelected, handleProductsSelected, setProductsSelected, resetProductSelected } = useSellsStatsBodyProductLogic({ selectedColumnsAndFilters, ...logic })
    const myData = useMemo(() => parseSellStatsByProduct(data?.selectedProducts || [], typeTableName), [data?.selectedProducts, typeTableName])
    myData.columns[0].customRender = (parameterValue) => <CustomRenderString value={translations[parameterValue]} />
    let renderThis: JSX.Element = <LoadingSpinner className='h-full' />
    if (data?.selectedProducts && myData.data) {
        renderThis = <TableWithoutVirtualListing data={myData.data} columns={myData.columns} />
    }

    return (
        <Fragment>
            <div className='px-20 pt-10 flex-auto h-1 flex-col flex'>
                <SellStatsHeader {...{ getData, selectedColumnsAndFilters, setSelectedColumnsAndFilters, typeStats, changeTypeStats, resetProductSelected, typeTableName }} children={
                    <Fragment>
                        {!selectedColumnsAndFilters?.filters?.ProductId?.value?.length ? <SearchBarProduct changeSelectedItems={handleProductsSelected} excludeIds={productsSelected} fieldNotIn='Product.id' name='ProductQuote' url='/api/app/product/getAllProductsIncludingSubproducts' id={''} placeholder={t('searchProductDots', 'Buscar producto...')} className='sell_stats_product_search_bar' where={{}} /> : null}
                        <ButtonModalConfigStatsProduct {...{ setSelectedColumnsAndFilters, selectedColumnsAndFilters, productsSelected, setProductsSelected, typeStats, typeTableName }} />
                    </Fragment>
                } />
                <div className='flex-auto mt-20 '>
                    {renderThis}
                </div>
            </div>
            <SellStatsBodyTotals myTotalAmountDict={myData.myTotalAmountDict} myData={myData.data} CurrencyId={myData.CurrencyId} />
        </Fragment>
    )
}

interface SellsStatsBodyProductLogicProps {
    typeSell: TypeSellTy
    urlGetData: string
    selectedColumnsAndFilters: SelectedColumnsAndFilters
}

const useSellsStatsBodyProductLogic = ({ selectedColumnsAndFilters, typeSell, urlGetData }: SellsStatsBodyProductLogicProps) => {
    const [productsSelected, setProductsSelected] = useState<number[]>([])
    const [data, setData] = useState<SellStatsProductData | null>(null)
    const resetProductSelected: any = useCallback((selectedColumnsAndFilters: SelectedColumnsAndFilters) => {
        if (selectedColumnsAndFilters?.filters?.ProductId?.value?.length > 0) {
            setProductsSelected([])
        }
    }, [])

    const handleProductsSelected = useCallback((Product: ProductExtendedTyShort) => {
        setProductsSelected((old) => {
            const copyOld = JSON.parse(JSON.stringify(old))
            copyOld.push(parseInt(String(Product?.id)))
            return [...copyOld]
        })
    }, [setProductsSelected])

    const getData = useCallback(async () => {
        const haveFiltersValue = selectedColumnsAndFilters.filters?.ProductId?.value.length > 0
        const myIds = haveFiltersValue ? selectedColumnsAndFilters.filters?.ProductId?.value : productsSelected
        let parseWhere: TwinDictionary = {
            'Product.id': { $in: myIds }
        }
        const startDate = selectedColumnsAndFilters.filters?.startDate?.value
        const endDate = selectedColumnsAndFilters.filters?.endDate?.value
        if (startDate && endDate) {
            parseWhere[typeSell+'Products.createdAt'] = { '$betweenN': [new Date(startDate), new Date(endDate)] }
        } else if (startDate) {
            parseWhere[typeSell+'Products.createdAt'] = { '$gteN': new Date(startDate) }
        } else if (endDate) {
            parseWhere[typeSell+'Products.createdAt'] = { '$lteN': new Date(endDate) }
        }
        const projectType = selectedColumnsAndFilters.filters?.projectType?.value
        if (projectType) {
            parseWhere[typeSell + 'Products.Order.CustomerProject.CustomerProjectTypeId'] = projectType
        }
        
        let result = await twinFetchPostJSON(urlGetData, {
            where: parseWhere,
            order: [['createdAt', 'ASC']]
        })
        if (result) {
            setData((old) => {
                const copy = JSON.parse(JSON.stringify(old))
                return { ...copy, selectedProducts: result }
            })
        }
    }, [selectedColumnsAndFilters, productsSelected, urlGetData, typeSell])

    useEffect(() => {
        getData()
    }, [getData])

    return { data, getData, setData, handleProductsSelected, productsSelected, setProductsSelected, resetProductSelected }
}

interface SellsStatsBodyCategoryProps extends SellStatsBodyBaseComponentsProps { }

const SellsStatsBodyCategory: React.FC<SellsStatsBodyCategoryProps> = ({ typeStats, changeTypeStats, typeTableName, ...logic }) => {
    const translations = useSellStatsColumns()
    const { selectedColumnsAndFilters, setSelectedColumnsAndFilters } = useConfigColumns({ name: typeTableName+'_product_config_stats', columns: [{ id: 'row', dataKey: 'row', label: '' }] })
    const { data, getData } = useSellsStatsBodyCategoryLogic({ selectedColumnsAndFilters, ...logic })
    const myData = useMemo(() => parseSellStatsByCategory(data?.selectedProducts || [], typeTableName), [data?.selectedProducts, typeTableName])
    myData.columns[0].customRender = (parameterValue) => <CustomRenderString value={translations[parameterValue]} />
    let renderThis: JSX.Element = <LoadingSpinner className='h-full'/>
    if (data?.selectedProducts) {
        renderThis = <TableWithoutVirtualListing data={myData.data} columns={myData.columns} />
    }

    return (
        <Fragment>
            <div className='px-20 pt-10 flex-auto h-1 flex-col flex'>
                <SellStatsHeader {...{ getData, selectedColumnsAndFilters, setSelectedColumnsAndFilters, typeStats, changeTypeStats, typeTableName }} />
                <div className='flex-auto mt-20 '>
                    {renderThis}
                </div>
            </div>
            <SellStatsBodyTotals myTotalAmountDict={myData.myTotalAmountDict} myData={myData.data} CurrencyId={myData.CurrencyId} />
        </Fragment>
    )
}

interface SellsStatsBodyCategoryLogicProps {
    typeSell: TypeSellTy
    urlGetData: string
    selectedColumnsAndFilters: SelectedColumnsAndFilters
}

const useSellsStatsBodyCategoryLogic = ({ selectedColumnsAndFilters, urlGetData, typeSell }: SellsStatsBodyCategoryLogicProps) => {
    const [data, setData] = useState<SellStatsProductData | null>(null)

    const getData = useCallback(async () => {
        let parseWhere: TwinDictionary = {}
        const startDate = selectedColumnsAndFilters.filters?.startDate?.value
        const endDate = selectedColumnsAndFilters.filters?.endDate?.value
        if (startDate && endDate) {
            parseWhere[typeSell + 'Products.createdAt'] = { '$betweenN': [new Date(startDate), new Date(endDate)] }
        } else if (startDate) {
            parseWhere[typeSell + 'Products.createdAt'] = { '$gteN': new Date(startDate) }
        } else if (endDate) {
            parseWhere[typeSell + 'Products.createdAt'] = { '$lteN': new Date(endDate) }
        }
        const projectType = selectedColumnsAndFilters.filters?.projectType?.value
        if (projectType) {
            parseWhere[typeSell + 'Products.Order.CustomerProject.CustomerProjectTypeId'] = projectType
        }
    
        let result = await twinFetchPostJSON(urlGetData, {
            where: parseWhere,
            order: [['createdAt', 'ASC']]
        })
        if (result) {
            setData((old) => {
                const copy = JSON.parse(JSON.stringify(old))
                return { ...copy, selectedProducts: result }
            })
        }
    }, [selectedColumnsAndFilters, urlGetData, typeSell])

    useEffect(() => {
        getData()
    }, [getData])

    return { data, getData, setData }
}

interface SellStatsHeaderProps extends SellStatsHeaderLogicProps {
    typeStats: TypeStatsTy
    changeTypeStats: (value: TypeStatsTy) => void
    selectedColumnsAndFilters: SelectedColumnsAndFilters
    setSelectedColumnsAndFilters: React.Dispatch<React.SetStateAction<SelectedColumnsAndFilters>>
    children?: React.ReactNode
    typeTableName: TypeSellTableNameTy
}

const SellStatsHeader: React.FC<SellStatsHeaderProps> = ({ selectedColumnsAndFilters, setSelectedColumnsAndFilters, typeStats, changeTypeStats, children, typeTableName, ...logic }) => {
    const { t } = useTwinTranslation()
    const { changeEndDate, changeStartDate, changeProjectType } = useSellStatsHeaderLogic({ setSelectedColumnsAndFilters, ...logic })
    const myCustomerProjectTypes = useMemo(() => addDictionaryExtraLine(JSON.parse(JSON.stringify(getAllCustomerProjectTypes())) || {}), [])
    const dictTypes = {
        'EmployeeId': {
            name: t('employee', 'Empleado')
        },
        'ProductId': {
            name: t('product', 'Producto')
        },
        'CategoryId': {
            name: t('category', 'Categoría')
        }
    }
    return (
        <div className='flex items-center gap-x-20 gap-y-10 flex-wrap'>
            <InputCalendarStateFull label={t('from', 'Desde')} onlyValids={true} value={selectedColumnsAndFilters.filters?.startDate?.value || ''} onChange={changeStartDate} />
            <InputCalendarStateFull label={t('to', 'Hasta')} onlyValids={true} value={selectedColumnsAndFilters.filters?.endDate?.value || ''} onChange={changeEndDate} />
            <CustomSelect className='w-120' label={t('type', 'Tipo')} items={dictTypes} value={typeStats} onChange={changeTypeStats} />
            {typeTableName !== 'sell_ticket' ? <CustomSelectWithSearchBar className='w-150' label={t('projectOppType', 'Tipo de proyecto/opportunidad')} items={myCustomerProjectTypes} onChange={changeProjectType} sortItems={true} sortFunction={emptyExtraLineFirstSortFunction} /> : null}
            {children}
        </div>
    )
}

interface SellStatsHeaderLogicProps {
    setSelectedColumnsAndFilters: React.Dispatch<React.SetStateAction<SelectedColumnsAndFilters>>
    resetProductSelected?: (selectedColumnsAndFilters: SelectedColumnsAndFilters) => void
}

const useSellStatsHeaderLogic = ({ setSelectedColumnsAndFilters, resetProductSelected }: SellStatsHeaderLogicProps) => {
    const changeStartDate = useCallback((value: string) => {
        flushSync(async () => {
            let selected: SelectedColumnsAndFilters | null = null
            await setSelectedColumnsAndFilters((old) => {
                const copy = JSON.parse(JSON.stringify(old))
                if (!copy.filters.endDate) {
                    copy.filters.endDate = { value: '' }
                }
                if (!copy.filters.startDate) {
                    copy.filters.startDate = { value: '' }
                }
                if (value !== '') {
                    const myVal = moment(value).startOf('day').toDate()
                    const endDate = copy?.filters?.endDate?.value
                    if (endDate !== '') {
                        const myEndDate = moment(endDate).toDate()
                        if (myEndDate < myVal) {
                            copy.filters.endDate.value = moment(value).endOf('day').toDate().toUTCString()
                        }
                    }
                    value = myVal.toUTCString()
                }
                copy.filters.startDate.value = value
                selected = copy
                return { ...copy }
            })
            if (selected) {
                resetProductSelected?.(selected)
            }
        })
    }, [setSelectedColumnsAndFilters, resetProductSelected])

    const changeEndDate = useCallback((value: string) => {
        flushSync(async () => {
            let selected: SelectedColumnsAndFilters | null = null
            await setSelectedColumnsAndFilters((old) => {
                const copy = JSON.parse(JSON.stringify(old))
                if (!copy.filters?.startDate) {
                    copy.filters.startDate = { value: '' }
                }
                if (!copy.filters?.endDate) {
                    copy.filters.endDate = { value: '' }
                }
                if (value !== '') {
                    const myVal = moment(value).endOf('day').toDate()
                    const startDate = copy.filters?.startDate?.value
                    if (startDate !== '') {
                        const myStartDate = moment(startDate).toDate()
                        if (myStartDate > myVal) {
                            copy.filters.startDate.value = moment(value).startOf('day').toDate().toUTCString()
                        }
                    }
                    value = myVal.toUTCString()
                }
                copy.filters.endDate.value = value
                selected = copy
                return { ...copy }
            })
            if (selected) {
                resetProductSelected?.(selected)
            }
        })
    }, [setSelectedColumnsAndFilters, resetProductSelected])

    const changeProjectType = useCallback((value: string) => {
        flushSync(async () => {
            let selected: SelectedColumnsAndFilters | null = null
            await setSelectedColumnsAndFilters((old) => {
                const copy = JSON.parse(JSON.stringify(old))
                if (!copy.filters?.projectType) {
                    copy.filters.projectType = { value: '' }
                }

                copy.filters.projectType.value = value
                selected = copy
                return { ...copy }
            })
            if (selected) {
                resetProductSelected?.(selected)
            }
        })
    }, [setSelectedColumnsAndFilters, resetProductSelected])

    return { changeStartDate, changeEndDate, changeProjectType }
}

interface SellStatsBodyTotalsProps {
    myTotalAmountDict: TwinDictionary
    myData: TwinDictionary[]
    CurrencyId: string
}

const SellStatsBodyTotals: React.FC<SellStatsBodyTotalsProps> = ({ myTotalAmountDict, myData, CurrencyId }) => {
    const translations = useSellStatsColumnsShort()
    const renderThis: JSX.Element[] = []
    for (const element of myData) {
        const elementRow = element.row || ''
        const item = myTotalAmountDict[elementRow]
        if (item){
            renderThis.push(
                <div className='flex items-center'>
                    <span className='regular16 mt-1'>{translations[elementRow]} ({item.quantity}):</span>
                    <DisplayPriceGettingParams CurrencyId={CurrencyId} value={item.amount} with_vat={false} className='regular20 ml-5'/>
                </div>
            )
        }
    }
    return (
        <div className='sell_stats_body_totals flex items-center py-17 px-20 gap-15 justify-end'>
            {renderThis}
        </div>
    )
}


const employeesDispatch = {
    setEmployees: (payload: EmployeesPayload) => ({ type: 'CHANGE_EMPLOYEE', payload }),
    setCustomerProjectTypes: (payload: CustomerProjectTypePayload) => ({ type: 'CHANGE_CUSTOMER_PROJECT_TYPES', payload })
}

export type ReduxEmployees = ConnectedProps<typeof employeesConnect>
const mapEmployeesConnector = (state: AllReduxPayloads) => ({ employees: state.employees, employeeAbsenceTypes: state.employeeAbsenceTypes, customerProjectTypes: state.customerProjectTypes })
const employeesConnect = connect(mapEmployeesConnector, employeesDispatch)
const employeesConnectLoading = withLoading(SellStatsBody, [{ fetchUrl: '/api/app/employee/getAllEmployees', propName: 'employees', setFunctionName: 'setEmployees' }, { fetchUrl: '/api/app/customer/customerProject/customerProjectTypes/getAllCustomerProjectTypesFull', propName: 'customerProjectTypes', setFunctionName: 'setCustomerProjectTypes' }])
const SellStatsBodyConnect = employeesConnect(employeesConnectLoading)


export default SellStatsBodyConnect